import { useCallback, useEffect, useMemo, useState } from 'react';
import { parseISO } from 'date-fns';
import { getIn, useFormikContext } from 'formik';
import type { ChangeEvent } from 'react';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import FormHelperText from '@mui/material/FormHelperText';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DatePicker } from '@mui/x-date-pickers';

import {
  OrganizationIdentifierKindEnum,
  getDocumentName,
  graphql,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import {
  formatOrganizationIdentifierInput,
  getOrganizationIdentifierKind,
  normalizeOrganizationIdentifierValue,
} from '@e-socna/third-parties';
import type {
  CurrencyId,
  LegalStatusId,
  ThirdPartyCategoryId,
} from '@e-socna/data-layer';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { AMOUNT_VALUE_FIELD_STEP } from '../../../../constants/number-field-steps.constant';
import { ImageUploadField } from '../../../../components/ImageUploadField';
import { generateId } from '../../../../utils/generate-id.util';
import { OrganizationPage_OneOrganization_QueryDocument } from '../../../../pages/Organization/hooks/useFindOneOrganizationQuery.hook';
import { IdMultiSelect, IdSelect } from '../../../../components/IdSelect';
import type { IdOption } from '../../../../components/IdSelect';

import { NafEntryAutocomplete } from '../../components/NafEntryAutocomplete';
import { LegalStatusFields } from '../../components/LegalStatusFields';
import type { ThirdPartyForm_Organization } from '../../fragments/organization.fragment';

export const ThirdPartyForm_OrganizationGeneralInfoStep_Organization_Fragment =
  graphql(`
    fragment ThirdPartyForm_OrganizationGeneralInfoStep_Organization_Fragment on Organization {
      code
      internalMemo
      memo
      birthDate
      isVip
      color
      categoryIds
      identifier {
        kind
        value
      }
      vatNumber
      shareCapital {
        _id
        value
        currencyId
      }
      name
      nafId
      tradingName
      legalStatusId
      activity
      logoImageUri
    }
  `);

interface Props {
  readonly id: string;
}

export const ThirdPartyOrganizationGeneralInfoFormStep =
  ({}: Readonly<Props>) => {
    const {
      thirdPartyCategories,
      currencies,
      fileDirectoryCategoriesCodeMap,
      thirdPartyFileCategoriesCodeMap,
    } = useReferencesContext();

    const {
      errors,
      handleBlur,
      handleChange,
      setFieldTouched,
      setFieldValue,
      setValues,
      touched,
      values,
    } = useFormikContext<ThirdPartyForm_Organization>();

    const switchAutoColor = useCallback(
      (_event: Readonly<ChangeEvent<HTMLInputElement>>, checked: boolean) => {
        if (checked) {
          return;
        }

        void setFieldValue('color', '#ffffff');
      },
      [setFieldValue],
    );

    const clearColor = useCallback(
      () => void setFieldValue('color', undefined),
      [setFieldValue],
    );

    const handleBirthDateChange = useCallback(
      (date: unknown) => void setFieldValue('birthDate', date),
      [setFieldValue],
    );

    const handleIdentifierChange = useCallback(
      (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
        const formattedValue = formatOrganizationIdentifierInput(
          event.target.value,
        );
        const identifierKind = getOrganizationIdentifierKind(
          normalizeOrganizationIdentifierValue(formattedValue),
        );

        void setValues((prevValues) => ({
          ...prevValues,
          identifier: {
            // `identifier` is nullable on the entity (a brand new Organization has none yet) -
            // fall back to a fresh id when there's nothing to spread from, rather than
            // producing an identifier missing `__typename`/`_id`.
            __typename: 'OrganizationIdentifier' as const,
            _id: prevValues.identifier?._id ?? generateId(),
            ...prevValues.identifier,
            value: formattedValue,
            kind:
              identifierKind === 'siret'
                ? OrganizationIdentifierKindEnum.Siret
                : OrganizationIdentifierKindEnum.Siren,
          },
        }));
      },
      [setValues],
    );

    const handleBirthDateBlur = useCallback(
      () => void setFieldTouched('birthDate', true),
      [setFieldTouched],
    );

    const birthDateValue = useMemo(
      () =>
        typeof values.birthDate === 'string'
          ? parseISO(values.birthDate)
          : values.birthDate,
      [values.birthDate],
    );

    const [isShareCapitalNeeded, setIsShareCapitalNeeded] = useState(false);

    useEffect(() => {
      // TODO : check if we can refactor this code in order to respect this rule
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsShareCapitalNeeded(
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        Boolean(values.shareCapital?.value || values.shareCapital?.currencyId),
      );
    }, [values]);

    const shareCapitalValueError =
      (getIn(errors, 'shareCapital.value') as string | undefined) ??
      (getIn(errors, 'shareCapital') as string | undefined);

    const shareCapitalCurrencyIdError =
      (getIn(errors, 'shareCapital.currencyId') as string | undefined) ??
      (getIn(errors, 'shareCapital') as string | undefined);

    const handleNafIdChange = useCallback(
      (nafId: string | null) =>
        void setFieldValue('nafId', nafId ?? EMPTY_STRING),
      [setFieldValue],
    );

    const handleCategoryIdsChange = useCallback(
      (ids: readonly ThirdPartyCategoryId[]) => {
        void setFieldValue('categoryIds', ids);
      },
      [setFieldValue],
    );

    const handleShareCapitalCurrencyIdChange = useCallback(
      (id: CurrencyId | typeof EMPTY_STRING) => {
        void setFieldValue('shareCapital.currencyId', id || EMPTY_STRING);
      },
      [setFieldValue],
    );

    const handleLegalStatusIdChange = useCallback(
      (id: string | null) => {
        void setFieldValue('legalStatusId', id ?? EMPTY_STRING);
      },
      [setFieldValue],
    );

    const handleNafIdBlur = useCallback(
      () => void setFieldTouched('nafId', true),
      [setFieldTouched],
    );

    const handleLogoImageUploadSuccess = useCallback(
      (fileUrl: string) => void setFieldValue('logoImageUri', fileUrl),
      [setFieldValue],
    );

    const handleDeleteLogoImage = useCallback(
      () => void setFieldValue('logoImageUri', EMPTY_STRING),
      [setFieldValue],
    );

    const organizationQueryKeys = useMemo<readonly (readonly string[])[]>(
      () => [
        [
          getDocumentName(OrganizationPage_OneOrganization_QueryDocument),
          values._id,
        ],
      ],
      [values._id],
    );

    return (
      <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
        {!values.isInternal && (
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <FormControl error={!!(errors.isVip && touched.isVip)} fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.isVip}
                    id="third-party-general-info-isVip"
                    name="isVip"
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                }
                label="Tiers VIP"
              />
              {!!(errors.isVip && touched.isVip) && (
                <FormHelperText>{errors.isVip}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <IdMultiSelect<ThirdPartyCategoryId, IdOption<ThirdPartyCategoryId>>
            error={!!(errors.categoryIds && touched.categoryIds)}
            helperText={
              !!(errors.categoryIds && touched.categoryIds) &&
              (getIn(errors, 'categoryIds') as string)
            }
            id="third-party-general-info-category-ids"
            label="Catégories du tiers"
            name="categoryIds"
            nullable
            onBlur={handleBlur}
            onChange={handleCategoryIdsChange}
            options={thirdPartyCategories.map((thirdPartyCategory) => ({
              id: thirdPartyCategory._id,
              label: thirdPartyCategory.label,
            }))}
            value={
              (values.categoryIds as readonly ThirdPartyCategoryId[] | null) ??
              EMPTY_ARRAY
            }
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={
              !!(
                getIn(errors, 'identifier.value') &&
                getIn(touched, 'identifier.value')
              )
            }
            fullWidth
            helperText={getTouchedError(errors, touched, 'identifier.value')}
            id="third-party-general-info-identifier-value"
            label={values.isInternal ? 'SIRET' : 'SIREN / SIRET'}
            name="identifier.value"
            onBlur={handleBlur}
            onChange={handleIdentifierChange}
            required={values.isInternal}
            type="text"
            value={values.identifier?.value ?? EMPTY_STRING}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={
              !!(getIn(errors, 'vatNumber') && getIn(touched, 'vatNumber'))
            }
            fullWidth
            helperText={getTouchedError(errors, touched, 'vatNumber')}
            id="third-party-general-info-vat-number"
            label="N° TVA Intracommunautaire"
            name="vatNumber"
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            value={values.vatNumber}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={!!(getIn(errors, 'name') && getIn(touched, 'name'))}
            fullWidth
            helperText={getTouchedError(errors, touched, 'name')}
            id="third-party-general-info-name"
            label="Raison sociale"
            name="name"
            onBlur={handleBlur}
            onChange={handleChange}
            required
            type="text"
            value={values.name}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={
              !!(getIn(errors, 'tradingName') && getIn(touched, 'tradingName'))
            }
            fullWidth
            helperText={getTouchedError(errors, touched, 'tradingName')}
            id="third-party-general-info-trading-name"
            label="Nom Commercial"
            name="tradingName"
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            value={values.tradingName}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={!!shareCapitalValueError}
            fullWidth
            helperText={shareCapitalValueError}
            id="third-party-general-info-share-capital-value"
            label="Capital social"
            name="shareCapital.value"
            onBlur={handleBlur}
            onChange={handleChange}
            required={isShareCapitalNeeded}
            slotProps={{
              htmlInput: {
                min: AMOUNT_VALUE_FIELD_STEP,
                step: AMOUNT_VALUE_FIELD_STEP,
              },
            }}
            type="number"
            value={values.shareCapital?.value}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <IdSelect<CurrencyId, IdOption<CurrencyId>>
            error={!!shareCapitalCurrencyIdError}
            helperText={shareCapitalCurrencyIdError}
            id="third-party-general-info-share-capital-currency-id"
            label="Devise"
            name="shareCapital.currencyId"
            nullable
            onBlur={handleBlur}
            onChange={handleShareCapitalCurrencyIdChange}
            options={currencies.map((currency) => ({
              id: currency._id,
              label: currency.label,
            }))}
            required={isShareCapitalNeeded}
            value={
              (values.shareCapital?.currencyId as CurrencyId | null) ??
              EMPTY_STRING
            }
          />
        </Grid>
        <LegalStatusFields
          error={
            !!(
              getIn(errors, 'legalStatusId') && getIn(touched, 'legalStatusId')
            )
          }
          helperText={
            (!!(
              getIn(errors, 'legalStatusId') && getIn(touched, 'legalStatusId')
            ) &&
              (getIn(errors, 'legalStatusId') as string)) ||
            undefined
          }
          name="legalStatusId"
          onBlur={handleBlur}
          onChange={handleLegalStatusIdChange}
          value={values.legalStatusId as LegalStatusId | null}
        />
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={!!getIn(errors, 'activity')}
            fullWidth
            helperText={getTouchedError(errors, touched, 'activity')}
            id="third-party-general-info-activity"
            label="Activité"
            name="activity"
            onBlur={handleBlur}
            onChange={handleChange}
            required={false}
            type="string"
            value={values.activity}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
          <NafEntryAutocomplete
            error={!!getIn(errors, 'nafId')}
            helperText={getTouchedError(errors, touched, 'nafId')}
            name="nafId"
            onBlur={handleNafIdBlur}
            onChange={handleNafIdChange}
            value={values.nafId}
          />
        </Grid>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <DatePicker
            displayWeekNumber
            label="Date de création"
            onChange={handleBirthDateChange}
            showDaysOutsideCurrentMonth
            slotProps={{
              field: {
                clearable: true,
              },
              actionBar: {
                actions: ['cancel', 'clear', 'today'],
              },
              textField: {
                error: !!getIn(errors, 'birthDate'),
                fullWidth: true,
                helperText: getIn(errors, 'birthDate') as string,
                id: 'birthDate',
                name: 'birthDate',
                onBlur: handleBirthDateBlur,
              },
            }}
            value={birthDateValue}
            views={['year', 'month', 'day']}
          />
        </Grid>
        <Grid
          size={12}
          sx={{ pt: 1, pb: 1, display: 'flex', alignItems: 'center' }}
        >
          {!values.color ? (
            <FormControl error={!!(errors.color && touched.color)} fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={!values.color}
                    id="third-party-general-info-auto-color"
                    name="color"
                    onBlur={handleBlur}
                    onChange={switchAutoColor}
                  />
                }
                label="Couleur automatique"
              />
              {!!(errors.color && touched.color) && (
                <FormHelperText>{errors.color}</FormHelperText>
              )}
            </FormControl>
          ) : (
            <>
              <TextField
                error={!!(getIn(errors, 'color') && getIn(touched, 'color'))}
                helperText={getTouchedError(errors, touched, 'color')}
                id="third-party-general-info-color"
                label="Couleur"
                name="color"
                onBlur={handleBlur}
                onChange={handleChange}
                sx={{ flexGrow: 1 }}
                type="color"
                value={values.color}
                variant="outlined"
              />
              <Box sx={{ ml: 1 }}>
                <Tooltip title="Générer la couleur automatiquement">
                  <IconButton
                    aria-label="Générer la couleur automatiquement"
                    onClick={clearColor}
                  >
                    <AutoAwesomeIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </Grid>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={
              !!(
                getIn(errors, 'internalMemo') && getIn(touched, 'internalMemo')
              )
            }
            fullWidth
            helperText={getTouchedError(errors, touched, 'internalMemo')}
            id="third-party-general-info-internalMemo"
            label="Mémo interne"
            multiline
            name="internalMemo"
            onBlur={handleBlur}
            onChange={handleChange}
            rows={4}
            type="text"
            value={values.internalMemo}
            variant="outlined"
          />
        </Grid>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <TextField
            error={!!(getIn(errors, 'memo') && getIn(touched, 'memo'))}
            fullWidth
            helperText={getTouchedError(errors, touched, 'memo')}
            id="third-party-general-info-memo"
            label="Mémo"
            multiline
            name="memo"
            onBlur={handleBlur}
            onChange={handleChange}
            rows={4}
            type="text"
            value={values.memo}
            variant="outlined"
          />
        </Grid>
        <Grid size={12} sx={{ pt: 2, pb: 1 }}>
          <ImageUploadField
            currentImageUri={values.logoImageUri}
            entityId={values._id}
            fileCategoryId={thirdPartyFileCategoriesCodeMap.ProfileImages._id}
            fileDirectoryCategoryId={
              fileDirectoryCategoriesCodeMap.ThirdParty._id
            }
            label="Logo"
            onDelete={handleDeleteLogoImage}
            onUploadSuccess={handleLogoImageUploadSuccess}
            queryKeysToInvalidate={organizationQueryKeys}
            showPreview
          />
        </Grid>
      </Grid>
    );
  };
